import { useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../api/client'
import { EmptyBlock, ErrorBlock, Pill } from '../components/ui'
import type { SearchResult } from '../types'

export default function FindRoute() {
  const [q, setQ] = useState('')
  const [submitted, setSubmitted] = useState('')
  const [state, setState] = useState<'idle' | 'loading' | 'ready' | 'empty' | 'error'>('idle')
  const [rows, setRows] = useState<SearchResult[]>([])
  const [err, setErr] = useState<{ m: string; c?: string } | null>(null)

  const run = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!q.trim()) return
    setSubmitted(q)
    setState('loading')
    try {
      const r = await api.search(q)
      setRows(r)
      setState(r.length ? 'ready' : 'empty')
    } catch (ex) {
      const x = ex as Error & { correlationId?: string }
      setErr({ m: x.message, c: x.correlationId })
      setState('error')
    }
  }

  return (
    <>
      <p className="eyebrow">Find</p>
      <h1 style={{ marginBottom: 'var(--s-sm)' }}>Find something</h1>
      <p className="note" style={{ marginBottom: 'var(--s-lg)' }}>
        One search box, across all twelve drives. You never need to know which drive
        a file lives in. Results only include what you can already see.
      </p>

      <form className="search" onSubmit={run} role="search">
        <label className="sr-only" htmlFor="q">Search files you can see</label>
        <input id="q" value={q} onChange={(e) => setQ(e.target.value)}
               placeholder="call centre contract" autoComplete="off" />
        <button className="btn btn--primary" type="submit">Search</button>
      </form>

      {state === 'idle' && (
        <EmptyBlock title="Search everything you can reach">
          Try a document name, a group address, or a country.
        </EmptyBlock>
      )}

      {state === 'loading' && (
        <div className="block block--narrow" aria-busy="true">
          <div className="skeleton" style={{ width: '55%', height: '1rem' }} />
          <div className="skeleton" style={{ width: '35%', height: '.75rem', marginTop: 8 }} />
        </div>
      )}

      {state === 'error' && err && <ErrorBlock message={err.m} correlationId={err.c} onRetry={() => setState('idle')} />}

      {/* Zero results is a designed state with a real explanation, not a blank page.
          Search returns nothing you lack permission to open, so "no results" and
          "no access" look identical from here. Say so. */}
      {state === 'empty' && (
        <EmptyBlock
          title={`Nothing you can see matches "${submitted}"`}
          action={<Link className="btn btn--secondary" to="/owners">Browse the folder map</Link>}
        >
          Search only covers files you already have access to, so this could mean the
          file does not exist, or that it does and you cannot open it. If you know
          roughly where it lives, the folder map names an owner you can ask.
        </EmptyBlock>
      )}

      {state === 'ready' && (
        <>
          <p className="note" style={{ marginBottom: 'var(--s-sm)' }}>
            {rows.length} result{rows.length === 1 ? '' : 's'}
          </p>
          <ul className="rowlist" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {rows.map((r) => (
              <li className="row" key={r.id}>
                <div className="row__main">
                  <div className="row__name">{r.name}</div>
                  <div className="row__meta">{r.driveName} · modified {r.modifiedTime}</div>
                  <div className="chips" style={{ marginTop: 6 }}>
                    {Object.entries(r.labels).map(([k, v]) => <Pill key={k}>{k.split(' ')[0]} · {v}</Pill>)}
                  </div>
                </div>
                <a className="btn btn--secondary" href={r.webViewLink} target="_blank" rel="noreferrer">Open</a>
              </li>
            ))}
          </ul>
        </>
      )}
    </>
  )
}
